import React, { useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import css from "./adm.module.css";

const SHOP_NAME = "Час майстрів";
const SHOP_COMPANY = "Час майстрів";
const SHOP_URL = "https://chas-maistriv.com.ua/";

const CATEGORY_ID = "1";
const CATEGORY_NAME = "Друкована продукція";
const CATEGORY_RZ_ID = "";

// якщо в товару немає жодного валідного фото
const FALLBACK_PICTURE = "https://chas-maistriv.com.ua/favicon.ico";

function xmlEscape(value = "") {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function cdataSafe(value = "") {
  return String(value).replace(/]]>/g, "]]]]><![CDATA[>");
}

function normalizeNumber(value, fallback = 0) {
  if (value === null || value === undefined || value === "") return fallback;
  const n = Number(String(value).replace(",", ".").trim());
  return Number.isFinite(n) ? n : fallback;
}

function firstNonEmpty(...values) {
  for (const v of values) {
    if (v === 0 || v === false) return v;
    if (v !== undefined && v !== null && String(v).trim() !== "") {
      return v;
    }
  }
  return "";
}

function toArray(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item.trim();
        if (item && typeof item === "object") {
          return String(item.url || item.src || item.downloadURL || "").trim();
        }
        return "";
      })
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed
            .map((item) => {
              if (typeof item === "string") return item.trim();
              if (item && typeof item === "object") {
                return String(
                  item.url || item.src || item.downloadURL || "",
                ).trim();
              }
              return "";
            })
            .filter(Boolean);
        }
      } catch (e) {}
    }

    return trimmed
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

function fieldToText(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item.trim();
        if (item && typeof item === "object") {
          return String(item.name || item.label || item.value || "").trim();
        }
        return String(item).trim();
      })
      .filter(Boolean)
      .join(", ");
  }

  if (value === undefined || value === null) return "";
  return String(value).trim();
}

function getValue(data, keys = [], fallback = "") {
  for (const key of keys) {
    const value = data?.[key];

    if (Array.isArray(value) && value.length) {
      return fieldToText(value);
    }

    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }

  return fallback;
}

function cleanOfferId(value, fallback = "BOOK1") {
  const cleaned = String(value || "")
    .replace(/[^A-Za-z0-9]/g, "")
    .trim();

  return cleaned || fallback;
}

function buildOfferId(docId, data, index) {
  return cleanOfferId(
    firstNonEmpty(data.cod, data.ean, data.ISBN, docId, `BOOK${index + 1}`),
    `BOOK${index + 1}`,
  );
}

function getStableRandomStock(offerId) {
  const str = String(offerId || "BOOK");
  let hash = 0;

  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) % 1000000;
  }

  return 20 + (hash % 40);
}

function buildPictures(data) {
  const mainPhotos = toArray(data.bookFoto);
  const extraPhotos = toArray(data.fotoRozgort);
  const images = toArray(data.images);
  const image = toArray(data.image);
  const photo = toArray(data.photo);

  const all = [...mainPhotos, ...extraPhotos, ...images, ...image, ...photo]
    .map((url) => String(url).trim())
    .filter(Boolean)
    .filter((url) => /^https?:\/\//i.test(url))
    .slice(0, 15);

  if (all.length) return all;

  return [FALLBACK_PICTURE];
}

function buildDescription(data) {
  return firstNonEmpty(
    data.longDesk,
    data.descriptionSe,
    data.smallDesc,
    data.description,
    "Опис книги відсутній.",
  );
}

function buildVendor(data) {
  return firstNonEmpty(data.publisher, data.vendor, data.brand, SHOP_COMPANY);
}

function buildName(data, offerId) {
  return firstNonEmpty(
    data.bookName,
    data.ceoName,
    data.name,
    `Книга ${offerId}`,
  );
}

function buildProductUrl(data, docId) {
  return firstNonEmpty(
    data.url,
    data.productUrl,
    data.link,
    `${SHOP_URL}product/${docId}`,
  );
}

function addParam(params, name, value) {
  if (value === undefined || value === null) return;
  const str = fieldToText(value);
  if (!str) return;
  params.push(`<param name="${xmlEscape(name)}">${xmlEscape(str)}</param>`);
}

function buildParams(data) {
  const params = [];

  addParam(params, "Тип", getValue(data, ["prodType", "productType"], "Книга"));
  addParam(params, "ISBN", getValue(data, ["ISBN"]));
  addParam(params, "EAN", getValue(data, ["ean", "EAN"]));
  addParam(params, "Автор", getValue(data, ["textAutor", "author"]));
  addParam(
    params,
    "Видавництво",
    getValue(data, ["publisher", "vendor", "brand"], "Час майстрів"),
  );
  addParam(params, "Серія видавництва", getValue(data, ["seria", "series"]));
  addParam(
    params,
    "Художник-ілюстратор",
    getValue(data, ["picWriter", "illustrator"]),
  );
  addParam(
    params,
    "Вік",
    getValue(data, ["yearGroupFor", "yearGroup", "age", "ages"]),
  );
  addParam(
    params,
    "Розділ",
    getValue(
      data,
      [
        "section",
        "rozetkaSection",
        "categoryName",
        "catalogSection",
        "prodType",
      ],
      "Пізнавальні та розвивальні книги",
    ),
  );
  addParam(
    params,
    "Мова",
    getValue(data, ["bookLanguage", "language"], "Українська"),
  );
  addParam(
    params,
    "Стать дитини",
    getValue(data, ["childGender", "gender", "forWho"]),
  );
  addParam(
    params,
    "Ілюстрації",
    getValue(data, ["ilystracii", "illustrations"]),
  );
  addParam(params, "Папір", getValue(data, ["bookPaper", "paper"]));
  addParam(params, "Палітурка", getValue(data, ["paliturka", "cover"]));
  addParam(
    params,
    "Кількість сторінок",
    getValue(data, ["pageCount", "pages"]),
  );
  addParam(params, "Формат", getValue(data, ["bookFormat", "format"]));
  addParam(params, "Рік видання", getValue(data, ["yearWrite", "publishYear"]));
  addParam(params, "Вага", getValue(data, ["booksWei", "weight"]));
  addParam(
    params,
    "Країна реєстрації бренду",
    getValue(data, ["brandCountry", "countryBrand"]),
  );
  addParam(
    params,
    "Країна-виробник товару",
    getValue(data, ["manufacturerCountry", "countryManufacturer", "country"]),
  );
  addParam(params, "Жанр", getValue(data, ["ganr", "genre"]));
  addParam(params, "Комплектація", getValue(data, ["complectation"]));
  addParam(params, "Герої книги", getValue(data, ["bookHero"]));

  if (!params.length) {
    params.push(`<param name="Тип">Книга</param>`);
  }

  return params.join("\n          ");
}

function buildRozetkaXml(rawProducts = []) {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const dateAttr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;

  const categoryTag = CATEGORY_RZ_ID
    ? `<category id="${xmlEscape(CATEGORY_ID)}" rz_id="${xmlEscape(CATEGORY_RZ_ID)}">${xmlEscape(CATEGORY_NAME)}</category>`
    : `<category id="${xmlEscape(CATEGORY_ID)}">${xmlEscape(CATEGORY_NAME)}</category>`;

  const offersArr = rawProducts.map((item, index) => {
    const offerId = buildOfferId(item.docId, item, index);
    const priceRaw = normalizeNumber(
      firstNonEmpty(item.price, item.priceSale, item.cost),
      1,
    );
    const price = priceRaw > 0 ? priceRaw : 1;
    const oldPrice = normalizeNumber(item.priceSale, 0);
    const stock = getStableRandomStock(offerId);
    const available = stock > 0 ? "true" : "false";
    const pictures = buildPictures(item);
    const name = buildName(item, offerId);
    const description = buildDescription(item);
    const vendor = buildVendor(item);
    const url = buildProductUrl(item, item.docId);
    const article = firstNonEmpty(item.cod, item.ean, item.ISBN);
    const params = buildParams(item);

    console.log("ROZETKA ITEM", {
      docId: item.docId,
      offerId,
      price,
      picturesCount: pictures.length,
      name,
      hasDescription: !!description,
      vendor,
    });

    return `
        <offer id="${xmlEscape(offerId)}" available="${available}">
          <stock_quantity>${stock}</stock_quantity>
          <url>${xmlEscape(url)}</url>
          <price>${price}</price>
          ${oldPrice > price ? `<price_old>${oldPrice}</price_old>` : ""}
          <currencyId>UAH</currencyId>
          <categoryId>${xmlEscape(CATEGORY_ID)}</categoryId>
          ${pictures.map((pic) => `<picture>${xmlEscape(pic)}</picture>`).join("\n          ")}
          <vendor>${xmlEscape(vendor)}</vendor>
          ${article ? `<article>${xmlEscape(article)}</article>` : ""}
          <name>${xmlEscape(name)}</name>
          <name_ua>${xmlEscape(name)}</name_ua>
          <description><![CDATA[${cdataSafe(description)}]]></description>
          <description_ua><![CDATA[${cdataSafe(description)}]]></description_ua>
          ${params}
        </offer>
      `.trim();
  });

  console.log("Всього товарів у XML:", offersArr.length);

  const offers = offersArr.join("\n");

  return `<?xml version="1.0" encoding="UTF-8"?>
<yml_catalog date="${dateAttr}">
  <shop>
    <name>${xmlEscape(SHOP_NAME)}</name>
    <company>${xmlEscape(SHOP_COMPANY)}</company>
    <url>${xmlEscape(SHOP_URL)}</url>

    <currencies>
      <currency id="UAH" rate="1"/>
    </currencies>

    <categories>
      ${categoryTag}
    </categories>

    <offers>
${offers}
    </offers>
  </shop>
</yml_catalog>`;
}

const DownloadRozetkaXmlButton = () => {
  const [loading, setLoading] = useState(false);

  const handleDownload = async () => {
    try {
      setLoading(true);

      const querySnapshot = await getDocs(collection(db, "product"));
      const products = [];

      querySnapshot.forEach((doc) => {
        products.push({
          docId: doc.id,
          ...doc.data(),
        });
      });

      console.log("З Firebase отримано товарів:", products.length);

      const xml = buildRozetkaXml(products);

      const blob = new Blob([xml], {
        type: "application/xml;charset=utf-8;",
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "rozetka-books.xml";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Помилка формування XML:", error);
      alert("Не вдалося сформувати XML для Rozetka");
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      className={css.listUlForAdmLiBut}
      onClick={handleDownload}
      disabled={loading}
    >
      {loading ? "Формую XML..." : "Завантажити XML для Rozetka"}
    </button>
  );
};

export default DownloadRozetkaXmlButton;
